import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Initialize retry count if not set
  if (!config.retryCount) {
    config.retryCount = 0;
  }

  return config;
});

// Response interceptor to handle 500 errors with retry logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Only retry on 500 errors
    if (error.response && error.response.status === 500) {
      // Check if we haven't exceeded max retries (3 attempts total)
      if (!config.retryCount || config.retryCount < 3) {
        config.retryCount = (config.retryCount || 0) + 1;

        console.log(`Retrying request (attempt ${config.retryCount}/3)...`);

        // Wait a bit before retrying (exponential backoff)
        const delay = Math.pow(2, config.retryCount - 1) * 1000; // 1s, 2s, 4s
        await new Promise(resolve => setTimeout(resolve, delay));

        // Retry the request
        return api(config);
      } else {
        // After 3 retries, redirect to error page
        console.error('Max retries exceeded, redirecting to error page');
        window.location.href = '/error?message=' + encodeURIComponent(error.message || 'Server Error');
        return Promise.reject(error);
      }
    }

    // For non-500 errors, reject normally
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data)
};

export const claimsAPI = {
  getAll: () => api.get('/claims'),
  getById: (id) => api.get(`/claims/${id}`),
  getCommunications: (id) => api.get(`/claims/${id}/communications`),
  getAttachments: (id) => api.get(`/claims/${id}/attachments`)
};

export const faqsAPI = {
  getAll: () => api.get('/faqs')
};

export const communicationsAPI = {
  getAll: () => api.get('/communications'),
  getUnreadCount: () => api.get('/communications/unread-count'),
  markAsRead: (id) => api.put(`/communications/${id}/read`)
};

export const attachmentsAPI = {
  getAll: () => api.get('/attachments'),
  upload: (formData) => {
    return api.post('/attachments/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (id) => api.delete(`/attachments/${id}`)
};

export const additionalInfoAPI = {
  getAll: () => api.get('/additional-info'),
  create: (data) => api.post('/additional-info', data)
};

export default api;
